import { Results, Helpers } from '../Base'
import AutosizeInput from 'react-input-autosize'
import Downshift from 'downshift'
import React, { Component } from 'react'
import Tag from './Tag'
import IconDown from '@components/Icon/IconDown'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { propTypes as mobxPropTypes } from 'mobx-react'
import debounce from 'lodash/debounce'

const KEYS = {
  BACKSPACE: 8,
  ENTER: 13,
  DELETE: 46,
  COMMA: 188,
  ARROW_UP: 38,
  ARROW_DOWN: 40
}

class TagsInput extends Component {
  static propTypes = {
    deleteRemoves: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    filterItemsOnly: PropTypes.bool, // if set to true it won't reorder results
    itemToString: PropTypes.func,
    loadOptions: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.oneOfType([
      PropTypes.array,
      mobxPropTypes.observableArray
    ]),
    renderItem: PropTypes.func,
    value: PropTypes.any.isRequired,
    onBlur: PropTypes.func,
    creatable: PropTypes.bool,
    itemFromCreatable: PropTypes.func,
    createdLabelKey: PropTypes.string,
    debounce: PropTypes.number
  }

  constructor (props) {
    super(props)
    this.state = {
      inputValue: '',
      inputFocused: false,
      items: this.props.options || []
    }

    if (props.debounce) {
      this.debouncedUpdate = debounce(this.asyncUpdateItems, props.debounce)
    }
  }

  onStateChange = (changes, downshiftStateAndHelpers) => {
    if (!downshiftStateAndHelpers.isOpen) {
      this.setState({ inputValue: '' })
    }

    if (changes.hasOwnProperty('inputValue')) {
      this.updateItems(changes.inputValue)
    }

    // handle stuff here if you need to
    // this is especially useful if you need
    // to controll some of the internal state yourself
  }

  onInputChange = event => {
    const updatedInputValue = event.target.value
    this.setState({ inputValue: updatedInputValue })
  }

  asyncUpdateItems = newInputValue => {
    this.setState({ loading: true })

    this.props
      .loadOptions(newInputValue)
      .then(items => {
        const sortedItems = this.handleResults(items, newInputValue)
        this.setState({
          loading: false,
          items: sortedItems
        })
      })
      .catch(error => {
        console.error(error)
        this.setState({
          loading: false,
          items: []
        })
      })
  }

  updateItems = newInputValue => {
    const isAsyncComponent = !!this.props.loadOptions

    if (isAsyncComponent) {
      this.debouncedUpdate
        ? this.debouncedUpdate(newInputValue)
        : this.asyncUpdateItems(newInputValue)
    } else {
      const filteredItems = this.handleResults(
        this.props.options,
        newInputValue
      )
      this.setState({
        items: filteredItems
      })
    }
  }

  handleResults = (items, inputValue) => {
    const { itemToString, filterItemsOnly } = this.props
    return Helpers.reorderItems({
      items,
      itemToString,
      inputValue,
      filterOnly: filterItemsOnly
    })
  }

  // triggered when new tag is selected
  handleSelection = newItem => {
    const selectedItems = this.props.value
    this.props.onChange([...selectedItems, newItem])
  }

  removeTag = item => {
    const copy = [...this.props.value]
    copy.splice(item.index, 1)
    this.props.onChange(copy)
  }

  removeLastTag () {
    const copy = [...this.props.value]
    const lastTagIndex = copy.length - 1
    copy.splice(lastTagIndex, 1)
    this.props.onChange(copy)
  }

  onInputKeyDown = event => {
    const currentValue = event.target.value
    const { creatable, createdLabelKey } = this.props
    const optionsExist = this.state.items.length > 0

    switch (event.keyCode) {
      case KEYS.BACKSPACE:
        if (!currentValue) {
          event.preventDefault()
          this.removeLastTag()
        }
        break

      // if (this.state.inputValue) {
      //   this.props.onChange(this.state.inputValue)
      //   this.setState({ inputValue: '' })
      // }

      // event.preventDefault()
      // event.stopPropagation()

      case KEYS.DELETE:
        if (!this.state.inputValue && this.props.deleteRemoves) {
          event.preventDefault()
          this.removeLastTag()
        }
        break

      case KEYS.ENTER:
        const labelKey = createdLabelKey || 'name'
        if (!optionsExist && !currentValue && creatable) {
          event.preventDefault()
          const itemExists = !!this.props.value.find(
            item => item[labelKey] === currentValue
          )
          if (!itemExists) {
            this.handleSelection({ [labelKey]: currentValue })
          }
        }
        // make sure to clean up the input on enter
        this.setState({ inputValue: '' })
        break

      // case KEYS.ARROW_UP:
      //   return

      // case KEYS.ARROW_DOWN:
      //   break

      default:
        return null
    }
  }

  onWrapperClick = e => {
    if (this._inputWrapper === e.target || this._input === e.target) {
      this.focusOnInput()
      e.stopPropagation()
      e.preventDefault()
    }
  }

  focusOnInput () {
    this._input.focus()
    if (typeof this._input.getInput === 'function') {
      this._input.getInput().focus()
    }
  }

  onInputGainFocus = () => {
    this.setState({ inputFocused: true })
  }

  onInputLoseFocus = () => {
    this.setState({ inputFocused: false })
    this.props.onBlur && this.props.onBlur()
  }

  inputRef = c => {
    this._input = c
  }

  inputWrapperRef = c => {
    this._inputWrapper = c
  }

  render () {
    const {
      renderItem,
      itemToString,
      value,
      error,
      disabled,
      itemFromCreatable,
      creatable,
      options
    } = this.props

    return (
      <Downshift
        defaultHighlightedIndex={0}
        itemToString={itemToString}
        onSelect={this.handleSelection}
        onStateChange={this.onStateChange}
        selectedItem={value}
        inputValue={this.state.inputValue}
        render={({
          getButtonProps,
          getInputProps,
          getItemProps,
          openMenu,
          highlightedIndex,
          inputValue,
          isOpen,
          selectedItem
        }) => {
          const containerClasses = classNames({
            'tags-input__container': true,
            autocomplete__container: true,
            'autocomplete__container--disabled': disabled,
            'tags-input__container--focus': this.state.inputFocused
          })

          const fakeInputClasses = classNames({
            autocomplete__input: true,
            'autocomplete__input--tags': true,
            'autocomplete__input--disabled': disabled,
            'autocomplete__input--error': error
          })

          const arrowIconClassName = classNames({
            'autocomplete__arrow-icon': true,
            'icon-animate': true,
            'icon-rotate': isOpen
          })

          const _inputProps = getInputProps({
            disabled: disabled,
            value: this.state.inputValue,
            ref: this.inputRef,
            inputClassName: 'tags-input__new-tag-input',
            onChange: this.onInputChange,
            onKeyDown: this.onInputKeyDown,
            onFocus: this.onInputGainFocus && openMenu,
            onBlur: this.onInputLoseFocus
          })

          const tagItems = (selectedItem || []).map((item, index) => {
            return { ...item, index }
          })

          const tags = tagItems.map(tag => (
            <Tag
              key={`Tag-${tag.index}`}
              onRemove={this.removeTag}
              itemToString={itemToString}
              tag={tag}
              disabled={disabled}
            />
          ))

          return (
            <div className={containerClasses}>
              <div
                className={fakeInputClasses}
                ref={this.inputWrapperRef}
                onClick={this.onWrapperClick}
                tabIndex='-1'
              >
                {tags}
                <AutosizeInput {..._inputProps} />
              </div>
              {!!options && (
                <IconDown
                  noClick={disabled}
                  className={arrowIconClassName}
                  {...getButtonProps({ disabled })}
                />
              )}
              <Results
                creatable={creatable}
                itemFromCreatable={itemFromCreatable}
                itemToString={itemToString}
                renderItem={renderItem}
                loading={this.state.loading}
                items={this.state.items}
                isOpen={isOpen}
                inputValue={inputValue}
                getItemProps={getItemProps}
                highlightedIndex={highlightedIndex}
                selectedItem={selectedItem}
                showResultsOnEmptyInput={!!options} // show items only if TagsInput has options locally
              />
            </div>
          )
        }}
      />
    )
  }
}

export default TagsInput
